import React from 'react';
import Box from '@mui/material/Box';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import Divider from '@mui/material/Divider';
import Timeline from '@mui/lab/Timeline';
import TimelineItem from '@mui/lab/TimelineItem';
import TimelineSeparator from '@mui/lab/TimelineSeparator';
import TimelineConnector from '@mui/lab/TimelineConnector';
import TimelineContent from '@mui/lab/TimelineContent';
import TimelineDot from '@mui/lab/TimelineDot';
import TimelineOppositeContent from '@mui/lab/TimelineOppositeContent';
import Card from '@mui/material/Card'; // [MỚI] Dùng Card để bọc ảnh cho đẹp
import { CaySamNhatKy } from 'src/types/caysam';
import { OptionModel } from 'src/types/api-response'; 

export type TimelineEntry = {
  time: string;
  note: string;
  images: [string, string];
};

type Props = {
  title?: string;
  items?: TimelineEntry[];
  itemsNhatKy?: CaySamNhatKy[];
  tinhTrangOptions?: OptionModel[];
  onImageClick?: (url: string) => void;
};

export default function TimeLine({ 
  title = 'Dòng thời gian', 
  items = [], 
  itemsNhatKy,
  tinhTrangOptions = [], 
  onImageClick 
}: Props) {

  const computedItems: TimelineEntry[] = React.useMemo(() => {
    if (itemsNhatKy && itemsNhatKy.length > 0) {
      return itemsNhatKy
        .filter((x): x is CaySamNhatKy => !!x)
        .map((nhatKy) => {
          const date = new Date(nhatKy.ngayGhi as unknown as string);
          const timeStr = date.toLocaleString('vi-VN', {
            year: 'numeric',
            month: '2-digit',
            day: '2-digit', 
            hour: '2-digit',
            minute: '2-digit',
          });
          
          const tinhTrangText = tinhTrangOptions.find(
            (opt) => Number(opt.value) === Number(nhatKy.tinhTrang)
          )?.text || nhatKy.tinhTrang; 

          // Tạo nội dung note có cấu trúc HTML/JSX để hiển thị đẹp hơn (tùy chọn)
          // Ở đây giữ nguyên string nhưng trình bày lại ở dưới
          const note = `Tình trạng: ${tinhTrangText} • Sức khỏe: ${nhatKy.diemSucKhoe} • Số lá: ${nhatKy.soLa}`;
          
          return {
            time: timeStr,
            note,
            images: [
              nhatKy.hinhAnhTongQuan || '/assets/placeholder.svg',
              nhatKy.hinhAnhChiTiet || '/assets/placeholder.svg',
            ],
          } as TimelineEntry;
        })
        .sort((a, b) => new Date(b.time).getTime() - new Date(a.time).getTime());
    }
    return items;
  }, [itemsNhatKy, items, tinhTrangOptions]);

  return (
    <Box>
      <Typography variant="subtitle1" sx={{ mb: 3, fontWeight: 'bold' }}>
        {title}  
      </Typography>

      {computedItems?.length === 0 ? (
        <Typography variant="body2" color="text.secondary">Chưa có dữ liệu.</Typography>
      ) : (
        <Timeline position="alternate" sx={{ p: 0, m: 0 }}>
          {computedItems.map((item, index) => (
            <React.Fragment key={`${item.time}-${index}`}>
              <TimelineItem sx={{ '&::before': { flex: 0, padding: 0 } }}> 
                {/* Lưu ý: &::before flex: 0 giúp timeline không bị đẩy quá xa nếu muốn căn chỉnh lại, 
                   tuy nhiên với position="alternate" thì MUI tự xử lý. 
                   Ở đây mình giữ nguyên layout nhưng chỉnh style content.
                */}
                
                <TimelineOppositeContent 
                  sx={{ m: 'auto 0', py: 2 }} 
                  align="right"
                  variant="body2" 
                  color="text.secondary"
                >
                  {item.time}
                </TimelineOppositeContent>

                <TimelineSeparator>
                  <TimelineConnector sx={{ bgcolor: 'primary.lighter' }} />
                  <TimelineDot color="primary" variant="outlined" sx={{ borderWidth: 2 }} />
                  <TimelineConnector sx={{ bgcolor: 'primary.lighter' }} />
                </TimelineSeparator>

                <TimelineContent sx={{ py: 2, px: 2 }}> 
                  {/* Card bao quanh nội dung để tạo khối rõ ràng */}
                  <Card 
                    sx={{ 
                      p: 2, 
                      bgcolor: 'background.neutral', 
                      boxShadow: 0,
                      border: '1px solid',
                      borderColor: 'divider'
                    }}
                  >
                    <Stack spacing={2}>
                      {/* Grid Layout cho 2 ảnh: Responsive và to hơn */}
                      <Box 
                        sx={{ 
                          display: 'grid', 
                          gridTemplateColumns: 'repeat(2, 1fr)', // Chia đôi cột
                          gap: 1.5 // Khoảng cách giữa 2 ảnh
                        }}
                      >
                        {item.images.map((imgUrl, imgIndex) => (
                          <Box
                            key={imgIndex}
                            component="img"
                            src={imgUrl}
                            alt={`timeline-${imgIndex}`}
                            onClick={() => onImageClick && onImageClick(imgUrl)}
                            sx={{
                              width: '100%',
                              height: '100%',
                              aspectRatio: '4/3', 
                              objectFit: 'cover',
                              borderRadius: 1,
                              cursor: 'pointer',
                              boxShadow: 2,
                              transition: 'all 0.2s ease-in-out',
                              '&:hover': {
                                transform: 'scale(1.02)', 
                                boxShadow: 4,
                                zIndex: 1
                              }
                            }}
                          />
                        ))}
                      </Box>

                      {/* Phần Note đưa xuống dưới, không dùng absolute nữa */}
                      <Box>
                        <Divider sx={{ borderStyle: 'dashed', mb: 1 }} />
                        <Typography variant="body2" sx={{ fontWeight: 'medium' }}>
                          {item.note}
                        </Typography>
                      </Box>
                    </Stack>
                  </Card>
                </TimelineContent>
              </TimelineItem>
            </React.Fragment>
          ))}
        </Timeline>
      )}   
    </Box>
  );
}